import { useState } from 'react';
import { Download, Mail, CreditCard, FileText, Loader2 } from 'lucide-react';
import { chatApi, errKey } from './chatAccessApi';
import { fmt } from './chatAccessI18n';
import { ErrorText, linkBtnCls } from './AccessSteps';
import { AppointmentCard } from '../appointments/AppointmentCard';
import { apptT } from '../appointments/apptI18n';

const smallBtn = 'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors disabled:opacity-50';

const DocRow = ({ doc, token, t, onError }) => {
  const [busy, setBusy] = useState('');
  const [sent, setSent] = useState('');
  const run = async (kind) => {
    setBusy(kind);
    onError('');
    try {
      if (kind === 'dl') await chatApi.download(token, doc);
      else setSent(fmt(t.sentTo, { email: (await chatApi.emailDoc(token, doc.id)).sent_to }));
    } catch (err) {
      onError(errKey(err) || 'generic');
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="border-t border-slate-100 pt-2" data-testid={`chat-doc-${doc.id}`}>
      <p className="flex items-start gap-1 text-xs font-medium break-all"><FileText className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#C9A227]" />{doc.name}
        {doc.is_new && <span className="ml-1 text-[10px] font-bold uppercase bg-[#C9A227] text-white rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`chat-doc-new-${doc.id}`}>{t.newBadge}</span>}
      </p>
      <div className="flex flex-wrap gap-2 mt-1">
        <button onClick={() => run('dl')} disabled={!!busy} className={`${smallBtn} bg-[#283880] text-white hover:bg-[#1f2c66]`} data-testid={`chat-doc-download-${doc.id}`}>
          {busy === 'dl' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}{t.download}
        </button>
        <button onClick={() => run('mail')} disabled={!!busy} className={`${smallBtn} bg-[#C9A227] text-white hover:bg-[#B8911F]`} data-testid={`chat-doc-email-${doc.id}`}>
          {busy === 'mail' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}{t.email}
        </button>
      </div>
      {sent && <p className="text-xs text-green-700 mt-1" data-testid={`chat-doc-sent-${doc.id}`}>{sent}</p>}
    </div>
  );
};

const PaymentRow = ({ p, token, t, onError }) => {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      window.location.href = (await chatApi.checkout(token, p.id)).checkout_url;
    } catch (err) {
      onError(errKey(err) || 'generic');
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-2" data-testid={`chat-payment-${p.id}`}>
      <p className="text-xs">{p.description}<span className="block text-slate-400">{p.pratica_numero}</span></p>
      <button onClick={pay} disabled={busy} className={`${smallBtn} bg-green-600 text-white hover:bg-green-700 whitespace-nowrap`} data-testid={`chat-payment-pay-${p.id}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}{t.pay} € {Number(p.amount).toFixed(2)}
      </button>
    </div>
  );
};

export const DocumentsView = ({ data, token, t, name, onLogout, onError, error, lang }) => {
  const withDocs = data.pratiche.filter((p) => p.documents.length > 0);
  const [appt, setAppt] = useState(data.next_appointment);
  return (
    <div className="space-y-3" data-testid="chat-documents-view">
      <p className="text-xs text-green-700" data-testid="chat-access-welcome">{fmt(t.welcome, { name })}</p>
      {appt && (
        <div className="bg-[#F8F5EA] border border-[#C9A227]/40 rounded-lg p-2" data-testid="chat-next-appointment">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#C9A227] mb-1">{apptT[lang]?.title || apptT.it.title}</p>
          <AppointmentCard appt={appt} lang={lang} compact
            onRespond={async (a, n) => {
              try { setAppt(await chatApi.respondAppt(token, appt.pratica_id, a, n)); } catch (err) { onError(errKey(err) || 'generic'); throw err; }
            }} />
        </div>
      )}
      {withDocs.length === 0 && <p className="text-xs text-slate-500" data-testid="chat-no-docs">{t.noDocs}</p>}
      {withDocs.map((p) => (
        <div key={p.id} data-testid={`chat-pratica-${p.id}`}>
          <p className="text-xs font-semibold text-[#283880]">{p.titolo}</p>
          <p className="text-[11px] text-slate-400">{p.numero_pratica}{p.status ? ` · ${t.status[p.status] || p.status}` : ''}</p>
          {p.documents.map((d) => <DocRow key={d.id} doc={d} token={token} t={t} onError={onError} />)}
        </div>
      ))}
      {data.payments.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[#283880]">{t.toPay}</p>
          {data.payments.map((p) => <PaymentRow key={p.id} p={p} token={token} t={t} onError={onError} />)}
        </div>
      )}
      <ErrorText text={error} />
      <button onClick={onLogout} className={linkBtnCls} data-testid="chat-access-logout">{t.logout}</button>
    </div>
  );
};
